import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdZoomIn, MdZoomOut, MdOpenInFull, MdCloseFullscreen } from 'react-icons/md';

import './FigureViewer.css';

/**
 * Bluebook-style viewer for SAT Math question figures. Presentational only —
 * holds local zoom/fullscreen state and never touches answers/save/submit.
 * Wrap it in <ToolErrorBoundary fallback={<img .../>}> at the call site so any
 * failure degrades to the plain image.
 */
const MIN_ZOOM = 25;
const MAX_ZOOM = 300;
const STEP = 25;

type Props = {
    src: string;
    alt?: string;
    /** class applied to the <img> so it keeps the existing figure sizing */
    imgClassName?: string;
};

export default function FigureViewer({ src, alt, imgClassName }: Props) {
    const [zoom, setZoom] = useState(100);
    const [fullscreen, setFullscreen] = useState(false);

    const clamp = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
    const zoomOut = () => setZoom((z) => clamp(z - STEP));
    const zoomIn = () => setZoom((z) => clamp(z + STEP));
    const reset = () => setZoom(100);

    // Esc closes fullscreen.
    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const toolbar = (isFullscreen: boolean) => (
        <div className="satFigureViewer__toolbar">
            <button
                type="button"
                className="satFigureViewer__btn"
                onClick={zoomIn}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Zoom in"
            >
                <MdZoomIn />
            </button>
            <button
                type="button"
                className="satFigureViewer__btn"
                onClick={zoomOut}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Zoom out"
            >
                <MdZoomOut />
            </button>
            <span className="satFigureViewer__pct">{zoom}%</span>
            <button
                type="button"
                className="satFigureViewer__reset"
                onClick={reset}
            >
                Reset
            </button>
            <span className="satFigureViewer__sep" />
            {isFullscreen ? (
                <button
                    type="button"
                    className="satFigureViewer__btn"
                    onClick={() => setFullscreen(false)}
                    aria-label="Exit fullscreen"
                >
                    <MdCloseFullscreen />
                </button>
            ) : (
                <button
                    type="button"
                    className="satFigureViewer__btn"
                    onClick={() => setFullscreen(true)}
                    aria-label="View fullscreen"
                >
                    <MdOpenInFull />
                </button>
            )}
        </div>
    );

    const image = (
        <img
            src={src}
            alt={alt || ''}
            draggable={false}
            className={imgClassName}
            /* Anchor zoom at top-left so a scaled-up figure spills down/right —
             * the only direction an overflow:auto container can scroll to. With
             * 'center top' the left overflow lands at a negative scroll offset
             * that's permanently unreachable, cutting the figure short. */
            style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
        />
    );

    return (
        <div className="satFigureViewer">
            {toolbar(false)}
            <div className="satFigureViewer__imgBox">{image}</div>

            {fullscreen &&
                createPortal(
                    <div
                        className="satFigureViewer__backdrop"
                        onClick={() => setFullscreen(false)}
                    >
                        <div
                            className="satFigureViewer__modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {toolbar(true)}
                            <div className="satFigureViewer__imgBox--full">{image}</div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
